"use client";
import { useState, useEffect, useCallback } from "react";

export interface GeolocationState {
    lat: number | null;
    lng: number | null;
    error: string | null;
    status: "idle" | "requesting" | "granted" | "denied" | "unavailable";
}

export const useGeolocation = () => {
    const [state, setState] = useState<GeolocationState>({
        lat: null,
        lng: null,
        error: null,
        status: "idle",
    });

    const requestLocation = useCallback(() => {
        if (!navigator.geolocation) {
            setState((s) => ({ ...s, status: "unavailable", error: "Geolocation not supported" }));
            return;
        }
        setState((s) => ({ ...s, status: "requesting", error: null }));
        navigator.geolocation.getCurrentPosition(
            (pos) => {
                setState({
                    lat: pos.coords.latitude,
                    lng: pos.coords.longitude,
                    error: null,
                    status: "granted",
                });
            },
            (err) => {
                setState((s) => ({
                    ...s,
                    status: "denied",
                    error: err.message || "Location access denied",
                }));
            },
            { enableHighAccuracy: true, timeout: 10000 }
        );
    }, []);

    return { ...state, requestLocation };
};

/** Haversine distance between two lat/lng points (in km) */
export const haversineDistanceKm = (
    lat1: number,
    lng1: number,
    lat2: number,
    lng2: number
): number => {
    const R = 6371;
    const dLat = ((lat2 - lat1) * Math.PI) / 180;
    const dLng = ((lng2 - lng1) * Math.PI) / 180;
    const a =
        Math.sin(dLat / 2) ** 2 +
        Math.cos((lat1 * Math.PI) / 180) *
        Math.cos((lat2 * Math.PI) / 180) *
        Math.sin(dLng / 2) ** 2;
    return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
};
